import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// ==================== GRAPHQL QUERIES ====================
const GET_ACTIVE_SPLASH = gql`
  query GetActiveSplashAnimation {
    getActiveSplashAnimation {
      _id
      name
      animationData
      duration
      backgroundColor
      tagline
      taglineColor
      companyLogo
      companyName
      poweredByText
      version
      createdAt
    }
  }
`;

const GET_ALL_SPLASH = gql`
  query GetAllSplashAnimations {
    getAllSplashAnimations {
      _id
      name
      description
      animationData
      duration
      backgroundColor
      tagline
      taglineColor
      companyLogo
      companyName
      poweredByText
      isActive
      version
      createdAt
      downloadCount
    }
  }
`;

const CREATE_SPLASH = gql`
  mutation CreateSplashAnimation($input: SplashAnimationInput!) {
    createSplashAnimation(input: $input) {
      _id
      name
      version
      isActive
    }
  }
`;

const ACTIVATE_SPLASH = gql`
  mutation ActivateSplashAnimation($id: ID!) {
    activateSplashAnimation(id: $id) {
      _id
      name
      isActive
    }
  }
`;

const DELETE_SPLASH = gql`
  mutation DeleteSplashAnimation($id: ID!) {
    deleteSplashAnimation(id: $id)
  }
`;

// ==================== MAIN COMPONENT ====================
const Splashscreen = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    duration: 5,
    backgroundColor: '#FFFFFF',
    tagline: 'your complete drone ecosystem',
    taglineColor: '#9F9F9F',
    companyName: 'Aviatricks',
    poweredByText: 'powered by',
    companyLogo: 'assets/images/Aviatricks_logo.png',
  });

  // Queries
  const { loading, data: allData, refetch } = useQuery(GET_ALL_SPLASH, {
    fetchPolicy: 'network-only',
  });
  
  const { data: activeData } = useQuery(GET_ACTIVE_SPLASH, {
    fetchPolicy: 'network-only',
  });

  // Mutations
  const [createAnimation] = useMutation(CREATE_SPLASH);
  const [activateAnimation] = useMutation(ACTIVATE_SPLASH);
  const [deleteAnimation] = useMutation(DELETE_SPLASH);

  const animations = allData?.getAllSplashAnimations || [];
  const activeAnimation = activeData?.getActiveSplashAnimation;

  // ========== FILE UPLOAD HANDLER ==========
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);

    if (file?.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          
          // Validate Lottie JSON
          if (!json.v || !json.fr || !json.layers) {
            alert('❌ Invalid Lottie JSON file!');
            return;
          }
          
          setJsonPreview(json);
          setFormData(prev => ({
            ...prev,
            name: prev.name || file.name.replace('.json', ''),
          }));
        } catch (err) {
          alert('❌ Invalid JSON file!');
        }
      };
      reader.readAsText(file);
    }
  };

  // ========== SUBMIT HANDLER ==========
  const handleSubmit = async () => {
    if (!jsonPreview) {
      alert('❌ Please upload a Lottie JSON file first!');
      return;
    }

    if (!formData.name.trim()) {
      alert('❌ Please enter an animation name!');
      return;
    }

    try {
      await createAnimation({
        variables: {
          input: {
            ...formData,
            animationData: jsonPreview,
            fileSize: new Blob([JSON.stringify(jsonPreview)]).size,
          },
        },
      });

      alert('✅ Animation uploaded and activated successfully!');
      setSelectedFile(null);
      setJsonPreview(null);
      setFormData({
        name: '',
        duration: 5,
        backgroundColor: '#FFFFFF',
        tagline: 'your complete drone ecosystem',
        taglineColor: '#9F9F9F',
        companyName: 'Aviatricks',
        poweredByText: 'powered by',
        companyLogo: 'assets/images/Aviatricks_logo.png',
      });
      refetch();
    } catch (err) {
      alert(`❌ Upload failed: ${err.message}`);
    }
  };

  // ========== ACTIVATE HANDLER ==========
  const handleActivate = async (id, name) => {
    if (window.confirm(`Activate "${name}" as splash screen?`)) {
      await activateAnimation({ variables: { id } });
      alert('✅ Animation activated!');
      refetch();
    }
  };

  // ========== DELETE HANDLER ==========
  const handleDelete = async (id, name, isActive) => {
    if (isActive) {
      alert('❌ Cannot delete active animation!');
      return;
    }
    if (window.confirm(`Delete "${name}"?`)) {
      await deleteAnimation({ variables: { id } });
      alert('🗑️ Animation deleted!');
      refetch();
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading animations...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🎬 Splash Animation Manager</h1>
          <p style={styles.subtitle}>Upload & manage your app's launch animations</p>
        </div>
      </div>

      {/* ACTIVE ANIMATION PREVIEW */}
      {activeAnimation && (
        <div style={styles.activeCard}>
          <div style={styles.activeHeader}>
            <h2 style={styles.activeTitle}>✨ Currently Active</h2>
            <span style={styles.activeBadge}>v{activeAnimation.version}</span>
          </div>
          
          <div style={styles.activeContent}>
            <div style={styles.activeInfo}>
              <p style={styles.activeName}>{activeAnimation.name}</p>
              <p style={{ ...styles.activeTagline, color: activeAnimation.taglineColor }}>
                {activeAnimation.tagline}
              </p>
              <p style={styles.activeMeta}>
                Duration: {activeAnimation.duration}s • 
                Uploaded: {new Date(activeAnimation.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* UPLOAD SECTION */}
      <div style={styles.uploadCard}>
        <h2 style={styles.sectionTitle}>📤 Upload New Animation</h2>
        
        <div style={styles.formGrid}>
          {/* File Upload */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Lottie JSON File *</label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              style={styles.fileInput}
              id="file-upload"
            />
            <label htmlFor="file-upload" style={styles.fileLabel}>
              {selectedFile ? selectedFile.name : 'Choose JSON file'}
            </label>
            {jsonPreview && (
              <div style={styles.validBadge}>
                ✅ Valid Lottie • {jsonPreview.w}x{jsonPreview.h} • {jsonPreview.layers?.length || 0} layers
              </div>
            )}
          </div>

          {/* Name */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Animation Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., New Year 2026"
              style={styles.input}
            />
          </div>

          {/* Duration */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Duration (seconds)</label>
            <input
              type="number"
              min="1"
              max="30"
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
              style={styles.input}
            />
          </div>

          {/* Background Color */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Background Color</label>
            <div style={styles.colorGroup}>
              <input
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => setFormData({...formData, backgroundColor: e.target.value})}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formData.backgroundColor}
                onChange={(e) => setFormData({...formData, backgroundColor: e.target.value})}
                style={styles.colorInput}
              />
            </div>
          </div>

          {/* Tagline */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Tagline</label>
            <input
              type="text"
              value={formData.tagline}
              onChange={(e) => setFormData({...formData, tagline: e.target.value})}
              style={styles.input}
            />
          </div>

          {/* Tagline Color */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Tagline Color</label>
            <div style={styles.colorGroup}>
              <input
                type="color"
                value={formData.taglineColor}
                onChange={(e) => setFormData({...formData, taglineColor: e.target.value})}
                style={styles.colorPicker}
              />
              <input
                type="text"
                value={formData.taglineColor}
                onChange={(e) => setFormData({...formData, taglineColor: e.target.value})}
                style={styles.colorInput}
              />
            </div>
          </div>

          {/* Company Name */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Company Name</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({...formData, companyName: e.target.value})}
              style={styles.input}
            />
          </div>

          {/* Powered By Text */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Powered By Text</label>
            <input
              type="text"
              value={formData.poweredByText}
              onChange={(e) => setFormData({...formData, poweredByText: e.target.value})}
              style={styles.input}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button 
          onClick={handleSubmit}
          disabled={!jsonPreview}
          style={{
            ...styles.submitBtn,
            opacity: !jsonPreview ? 0.5 : 1,
            cursor: !jsonPreview ? 'not-allowed' : 'pointer',
          }}
        >
          🚀 Upload & Activate
        </button>
      </div>

      {/* ALL ANIMATIONS LIST */}
      <div style={styles.listCard}>
        <h2 style={styles.sectionTitle}>📋 All Animations ({animations.length})</h2>
        
        <div style={styles.grid}>
          {animations.map((anim) => (
            <div 
              key={anim._id} 
              style={{
                ...styles.animationCard,
                border: anim.isActive ? '3px solid #4CAF50' : '1px solid #e0e0e0',
              }}
            >
              {anim.isActive && <span style={styles.activeBadgeSmall}>ACTIVE</span>}
              
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>{anim.name}</h3>
                <span style={styles.cardVersion}>v{anim.version}</span>
              </div>

              <div style={{
                ...styles.previewPlaceholder,
                backgroundColor: anim.backgroundColor,
              }}>
                🎬
              </div>

              <div style={styles.cardDetails}>
                <p style={styles.detailRow}>
                  <span style={styles.detailLabel}>Duration:</span> {anim.duration}s
                </p>
                <p style={{ ...styles.detailRow, color: anim.taglineColor }}>
                  <span style={styles.detailLabel}>Tagline:</span> {anim.tagline}
                </p>
                <p style={styles.detailRow}>
                  <span style={styles.detailLabel}>Downloads:</span> {anim.downloadCount || 0}
                </p>
                <p style={styles.detailRow}>
                  <span style={styles.detailLabel}>Created:</span> {new Date(anim.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div style={styles.cardActions}>
                {!anim.isActive && (
                  <button
                    onClick={() => handleActivate(anim._id, anim.name)}
                    style={styles.activateBtn}
                    title="Activate"
                  >
                    ✅ Activate
                  </button>
                )}
                
                <button
                  onClick={() => handleDelete(anim._id, anim.name, anim.isActive)}
                  style={styles.deleteBtn}
                  title="Delete"
                  disabled={anim.isActive}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== STYLES - NO DUPLICATES ====================
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '30px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    margin: 0,
  },
  
  // Active Card Styles
  activeCard: {
    background: 'linear-gradient(145deg, #667eea, #764ba2)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '30px',
    color: 'white',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
  },
  activeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  activeTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
    color: 'white',
  },
  activeBadge: {
    background: 'rgba(255,255,255,0.2)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
  },
  activeContent: {
    display: 'flex',
    alignItems: 'center',
  },
  activeInfo: {
    flex: 1,
  },
  activeName: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: 'white',
  },
  activeTagline: {
    fontSize: '16px',
    margin: '0 0 8px 0',
    opacity: 0.9,
  },
  activeMeta: {
    fontSize: '13px',
    opacity: 0.8,
    margin: '4px 0',
    color: 'rgba(255,255,255,0.8)',
  },

  // Upload Card Styles
  uploadCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '30px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    border: '1px solid #e0e0e0',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 24px 0',
    color: '#1a1a1a',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333',
  },
  input: {
    padding: '10px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  fileInput: {
    display: 'none',
  },
  fileLabel: {
    padding: '12px',
    background: '#f5f5f5',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
  },
  validBadge: {
    marginTop: '8px',
    padding: '6px 12px',
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '4px',
    fontSize: '12px',
  },
  colorGroup: {
    display: 'flex',
    gap: '8px',
  },
  colorPicker: {
    width: '50px',
    height: '42px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  colorInput: {
    flex: 1,
    padding: '10px 12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(145deg, #667eea, #764ba2)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // List Card Styles
  listCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    border: '1px solid #e0e0e0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  animationCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '16px',
    position: 'relative',
  },
  activeBadgeSmall: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: '#4CAF50',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
    color: '#1a1a1a',
  },
  cardVersion: {
    fontSize: '12px',
    color: '#666',
    background: '#f5f5f5',
    padding: '4px 8px',
    borderRadius: '12px',
  },
  previewPlaceholder: {
    width: '100%',
    height: '120px',
    borderRadius: '8px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: '#999',
  },
  cardDetails: {
    marginBottom: '16px',
  },
  detailRow: {
    fontSize: '13px',
    margin: '4px 0',
    color: '#666',
  },
  detailLabel: {
    color: '#999',
    marginRight: '4px',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  activateBtn: {
    padding: '8px 12px',
    border: 'none',
    background: '#e8f5e9',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#2e7d32',
    fontWeight: '500',
  },
  deleteBtn: {
    padding: '8px 12px',
    border: 'none',
    background: '#ffebee',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#c62828',
    fontWeight: '500',
  },
  
  // Loading Styles
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
};

// Add keyframes for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Splashscreen;